import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
} from '@mui/material';

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [fullName, setFullName] = useState('');
  const [organisationName, setOrganisationName] = useState('');
  const [country, setCountry] = useState('UK');
  const [siteCount, setSiteCount] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const logoUrl =
    'https://horizons-cdn.hostinger.com/f34f6b4a-5701-45b6-9778-48a500b88e4d/00c7de63c68d9d1ea880547b4c0c04a6.png';
  const leftBg =
    'https://images.pexels.com/photos/262978/pexels-photo-262978.jpeg?auto=compress&cs=tinysrgb&w=1600';

  useEffect(() => {
    const fetchInvite = async () => {
      setLoading(true);
      setError('');
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5008';
        const res = await fetch(`${baseUrl}/api/invites/${token}`);
        const body = await res.json().catch(() => ({}));

        if (!res.ok || !body.status) {
          throw new Error(body.message || 'Invite not found or no longer valid.');
        }

        setInvite(body.invite);
        setFullName(body.invite.name || '');
      } catch (err) {
        setError(err.message || 'Unable to load invite.');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchInvite();
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!organisationName) {
      setError('Organisation name is required.');
      return;
    }
    if (!country) {
      setError('Country is required.');
      return;
    }
    if (!fullName) {
      setError('Full name is required.');
      return;
    }
    if (!jobTitle) {
      setError('Job title is required.');
      return;
    }
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setSubmitting(true);
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5008';
      const res = await fetch(`${baseUrl}/api/invites/${token}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          organisationName,
          country,
          siteCount,
          fullName,
          jobTitle,
          password,
        }),
      });

      const body = await res.json().catch(() => ({}));

      if (!res.ok || !body.status) {
        throw new Error(body.message || 'Unable to accept invite.');
      }

      setSuccessMessage('Your organisation and admin account have been created. You can now log in.');

      setTimeout(() => {
        navigate('/login');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Something went wrong while accepting the invite.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Grid container sx={{ minHeight: '100vh' }}>
        {/* Left side – reuse Dexign-style branding panel */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            p: 4,
            backgroundImage: `linear-gradient(to bottom, rgba(15,23,42,0.75), rgba(15,23,42,0.9)), url(${leftBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            color: '#fff',
          }}
        >
          <Box>
            <Box sx={{ mb: 4 }}>
              <img src={logoUrl} alt="RTNZERO" style={{ width: 140 }} />
            </Box>
            <Box sx={{ maxWidth: 420 }}>
              <Typography variant="h4" fontWeight="bold" sx={{ mb: 2 }}>
                Your invite is ready to go live.
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Confirm your organisation details and create a super admin login to unlock your
                RTNZERO workspace.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 4, fontSize: 12, opacity: 0.85 }}>
            <Typography variant="body2">Need help? Contact support@rtnzero.ai</Typography>
          </Box>
        </Grid>

        {/* Right side – invite acceptance card */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 3, md: 6 },
          }}
        >
          <Container maxWidth="sm">
            <Paper
              elevation={8}
              sx={{
                p: 4,
                borderRadius: 3,
                boxShadow: '0 20px 60px rgba(15,23,42,0.65)',
              }}
            >
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Accept your RTNZERO invite
              </Typography>

              {loading && (
                <Typography variant="body2" color="text.secondary">
                  Loading invite…
                </Typography>
              )}

              {!loading && error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {!loading && !error && invite && (
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    You&apos;ve been invited to create your RTNZERO workspace. Confirm your details,
                    set up your organisation and create your super admin login.
                  </Typography>

                  <TextField
                    fullWidth
                    label="Email"
                    value={invite.email}
                    disabled
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Organisation name"
                    value={organisationName}
                    onChange={(e) => setOrganisationName(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Number of sites (optional)"
                    type="number"
                    value={siteCount}
                    onChange={(e) => setSiteCount(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Full name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Job title (Owner / Manager / Director / etc.)"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Confirm password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  {successMessage && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {successMessage}
                    </Alert>
                  )}

                  {error && !successMessage && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={submitting}
                    sx={{ mt: 1, borderRadius: 999 }}
                  >
                    {submitting ? 'Saving…' : 'Create account'}
                  </Button>
                </Box>
              )}
            </Paper>
          </Container>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AcceptInvite;

